import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import type { ChangeEvent, Ref } from 'react';
import { z } from 'zod';
import type { FormFieldBaseProps } from '../FormField/FormField.types';

export interface IPSubnetForm {
  ipAddress: string;
  search: string;
}

export type IPSubnetInputType = 'ip' | 'cidr' | 'ip/cidr';

export interface ValidationResult {
  // If a message is undefined, it means that the validation passed successfully.
  message?: string;
  validAddresses: string[];
  invalidAddresses: string[];
}

export type IPSubnetInputValue = {
  /** List of parsed IP addresses in the input field */
  ips: string[];
};

export type IPSubnetInputValueWithError = IPSubnetInputValue & {
  /** Value of the text input internally used to populate IP addresses, for purposes of validation */
  invalidInput: string;
  /** Error message describing the particular issue with the invalid user input */
  message: string;
};

export const ipSubnetInputValueSchema = z
  .object({
    ips: z.array(z.string()),
    invalidInput: z.string().optional(),
    message: z.string().optional(),
  })
  .strict();

export type IPSubnetInputValueOrDraft =
  | IPSubnetInputValueWithError
  | IPSubnetInputValue;

export const isIpSubnetInputValidValue = (
  value: IPSubnetInputValueOrDraft,
): value is IPSubnetInputValue =>
  (value as IPSubnetInputValueWithError).invalidInput === undefined &&
  (value as IPSubnetInputValueWithError).message === undefined;

/**
 *
 * @example
 * ```
 * z
 *  .custom<IPSubnetInputValueOrDraft>()
 *  .refine(isIpSubnetInputValidValue, getIPSubnetZodValidationError),
 * ```
 */
export const getIPSubnetZodValidationError = (
  value: IPSubnetInputValueOrDraft,
): z.CustomErrorParams => {
  if (hasIPSubnetInputValueError(value)) {
    return { message: value.message };
  }
  stumble(
    'IPSubnetInput value expected to be already checked to be invalid value',
  );
  return { message: 'Internal validation error' };
};

export function hasIPSubnetInputValueError(
  value: IPSubnetInputValueOrDraft,
): value is IPSubnetInputValueWithError {
  return (
    (value as IPSubnetInputValueWithError).invalidInput !== undefined &&
    (value as IPSubnetInputValueWithError).message !== undefined
  );
}

export interface IPSubnetInputProps {
  /**
   * The `value` prop is a composite type that can represent either:
   * - a list of valid IP addresses (`IPSubnetInputValue`), or
   * - a draft value additionally containing the user's invalid input and a validation message (`IPSubnetInputValueWithError`).
   */
  value: IPSubnetInputValueOrDraft;
  /**
   * The error message which will be shown right below the input field.
   * Note: The parent component (such as a form wrapper) is responsible for validating the value and passing any error messages to the form infrastructure.
   */
  error?: string;
  warning?: string;
  hasError?: boolean;
  searchable?: boolean;
  className?: string;

  name?: string;
  placeholder?: string;
  /**
   * Callback triggered when the user changes the input value.
   * Receives an object containing valid IP addresses, any invalid user input, and a validation message if applicable.
   * Note: The parent component (such as a form wrapper) is responsible for handling the onChange event and displaying error messages within the form infrastructure.
   */
  onChange: (value: IPSubnetInputValueOrDraft) => void;
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
  inputRef?: Ref<HTMLInputElement>;
  disabled?: boolean;

  type?: IPSubnetInputType;
  /**
   * An optional custom validation function for additional checks.
   * This function is invoked after the default validation and receives as its argument the list of valid addresses returned by the default validation.
   * Both default and custom validations are performed before adding the parsed TextField values to the list of subnets.
   */
  customValidation?: (ips: string[]) => ValidationResult;
  onValidate?: (result: ValidationResult) => void;
  inputId?: string;
}

export type IPSubnetFieldType = IPSubnetInputProps &
  Omit<FormFieldBaseProps, 'size'>;
